import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { FormattedMessage } from "react-intl";

import styles from "@components/account/Account.module.css";
import Container from "@components/Container";
import ListItem from "@components/ListItem";
import MobileHeader from "@components/MobileHeader";
import Icon from "@components/text/Icon";
import Heading2 from "@components/typography/Heading2";
import useMyClient from "@hooks/useMyClient";
import { AccountSectionEnum } from "@typing/Enums";
import { IconName } from "@utils/iconUtils";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  currentSection: AccountSectionEnum | null;
  handleCurrentSectionChange: (section: AccountSectionEnum, direction: "left" | "right") => void;
  journeyId: string;
};

const InnerContent = ({ section }: { section: AccountSectionEnum }) => {
  const iconForSection = (section: AccountSectionEnum): IconName => {
    switch (section) {
      case AccountSectionEnum.INFO:
        return "user";
      case AccountSectionEnum.PRIVACY:
        return "gear";
      case AccountSectionEnum.PACKAGE:
        return "basketShopping";
      case AccountSectionEnum.CONTACT:
        return "messagesQuestion";
      case AccountSectionEnum.REPORT_A_PROBLEM:
        return "sealExclamation";
      case AccountSectionEnum.FAQ:
        return "infoCircle";
      default:
        return "infoCircle";
    }
  };

  return (
    <IonGrid className="ion-no-padding">
      <IonRow className="ion-align-items-center">
        <IonCol className="flex items-center ion-margin-end" size="auto">
          <Icon color="secondary" icon={iconForSection(section)} size="large" />
        </IonCol>
        <IonCol>
          <Heading2 bold color="black">
            <FormattedMessage id={`pages.client.account.menu.${section}`} />
          </Heading2>
        </IonCol>
        <IonCol size="auto">
          <Icon color="gray-150" icon="chevronRight" size="large" />
        </IonCol>
      </IonRow>
    </IonGrid>
  );
};

const AccountMenu = ({ currentSection, handleCurrentSectionChange, journeyId }: Props) => {
  const myClient = useMyClient();

  const buttonOrder = [
    AccountSectionEnum.INFO,
    AccountSectionEnum.PRIVACY,
    AccountSectionEnum.PACKAGE,
    AccountSectionEnum.CONTACT,
    AccountSectionEnum.REPORT_A_PROBLEM,
    AccountSectionEnum.FAQ
  ];

  const isExternalSection = (section: AccountSectionEnum) =>
    section === AccountSectionEnum.CONTACT || section === AccountSectionEnum.FAQ;

  return (
    <div>
      <MobileHeader
        routerLink={journeyRoute({ journeyId, linkAfterJourney: "/home", myClient })}
        titleKey="pages.client.account.title"
      />
      <Container className={styles.menuContainer}>
        {buttonOrder.map(section => (
          <ListItem
            button
            href={
              section === AccountSectionEnum.CONTACT
                ? "mailto:support@withgrayce.com"
                : section === AccountSectionEnum.FAQ
                  ? "https://www.withgrayce.com/faq"
                  : undefined
            }
            key={section}
            onClick={() => !isExternalSection(section) && handleCurrentSectionChange(section, "right")}
            rel={isExternalSection(section) ? "noopener noreferrer" : undefined}
            selected={currentSection === section}
            target={isExternalSection(section) ? "_blank" : undefined}
          >
            <InnerContent section={section} />
          </ListItem>
        ))}
      </Container>
    </div>
  );
};

export default AccountMenu;
